import React from 'react';
import { SidebarIcons, SIDEBAR_ITEMS, parseRoute } from './Sidebar.jsx';
import {
  LensBacktestDateRangeControl,
  LensBookShelfScopeSelector,
  LensLiveUpdateControl,
  LensOpenLedgerLink,
  LensReportingCurrencyControl,
} from './LensTopbarControls.jsx';
import { DASHBOARD_VERSION, SNAPSHOT_AS_OF } from '../data.js';
import {
  formatSourceStateLabel,
  formatSourceStateTooltip,
  normalizeSourceState,
} from '../source-state.js';

export default function Topbar({
  activeTab,
  sourceState,
  ledgerHandoffContext,
  bookScopeCatalog,
  bookScopeSelection,
  bookScopeLoading,
  bookScopeError,
  actualsDateRange,
  ledgerReady = false,
  ledgerRefreshing = false,
  onBookScopeChange,
  onActualsDateRangeChange,
  onRefreshLedger,
  onToggleSidebar,
  onShowHelp,
}) {
  const { moduleId } = parseRoute(activeTab);
  const item = SIDEBAR_ITEMS.find(t => t.id === moduleId);
  const normalizedSource = normalizeSourceState(sourceState);
  const ledgerBaseUrl = typeof import.meta !== 'undefined'
    ? import.meta.env?.VITE_LEDGER_URL
    : '';
  const pill = 'inline-flex items-center text-[0.68rem] px-[7px] py-[2px] rounded-xl bg-surface-raised text-text-muted border border-border tracking-[0.04em] font-[inherit] transition-all duration-150';
  const pillSnapshot = `${pill} cursor-default uppercase !text-accent-gold !border-yellow-soft !bg-[rgba(240,180,41,0.08)]`;
  return (
    <header className="sticky top-0 flex flex-wrap justify-between items-center gap-2 px-4 py-[0.45rem] bg-bg border-b border-border z-[25] shrink-0">
      <div className="flex min-w-[180px] flex-1 items-center gap-[0.65rem]">
        <button
          type="button"
          className="bg-surface-el border border-border-strong text-text-muted px-[0.55rem] py-[0.3rem] rounded-md cursor-pointer inline-flex items-center gap-[0.35rem] whitespace-nowrap hover:text-text hover:border-accent-gold hover:bg-[rgba(240,180,41,0.08)] transition-colors [&_svg]:w-[15px] [&_svg]:h-[15px] [&_svg]:shrink-0"
          onClick={onToggleSidebar}
          title="Toggle sidebar ( [ )"
          aria-label="Toggle sidebar"
        >
          {SidebarIcons.collapse}
          <span className="text-[0.72rem] font-semibold tracking-[0.04em]">Menu</span>
          <span className="text-[0.62rem] text-text-dim font-mono px-[5px] py-[1px] rounded-[3px] border border-border">[</span>
        </button>
        <div className="flex items-baseline gap-2 flex-wrap min-w-0">
          <span className="font-semibold text-text text-[0.86rem]">Cleic Lens</span>
          <span className="text-text-dim">›</span>
          <span className="text-accent-gold font-semibold text-[0.86rem]">{item?.label || activeTab}</span>
          <em className="hidden max-w-[360px] truncate text-text-muted text-[0.72rem] not-italic xl:inline">{item?.hint}</em>
        </div>
      </div>
      <div
        className="flex max-w-full flex-wrap items-center justify-start gap-[0.4rem] sm:justify-end"
        data-ux-pattern="lens-global-filterbar-parity-v01"
        data-source-marker="ledger-lens-global-filterbar-parity-20260518"
      >
        <LensBookShelfScopeSelector
          catalog={bookScopeCatalog}
          selection={bookScopeSelection}
          loading={bookScopeLoading}
          error={bookScopeError}
          onChange={onBookScopeChange}
        />
        <LensReportingCurrencyControl
          sourceState={normalizedSource}
          handoffContext={ledgerHandoffContext}
          catalog={bookScopeCatalog}
          selection={bookScopeSelection}
        />
        <LensBacktestDateRangeControl
          value={actualsDateRange}
          onChange={onActualsDateRangeChange}
        />
        <LensLiveUpdateControl
          sourceState={normalizedSource}
          ledgerReady={ledgerReady}
          refreshing={ledgerRefreshing}
          onRefresh={onRefreshLedger}
        />
        <span className={pillSnapshot} title={`Snapshot as of ${SNAPSHOT_AS_OF}`}>
          v{DASHBOARD_VERSION.replace(/^v/, '')}
        </span>
        <span className={pillSnapshot} title={formatSourceStateTooltip(normalizedSource)}>
          {formatSourceStateLabel(normalizedSource, { compact: true })}
        </span>
        <span className={pillSnapshot} title="Private operator view. Public/client/strict real-book claims remain HOLD until separately approved.">
          private workspace
        </span>
        <LensOpenLedgerLink
          ledgerBaseUrl={ledgerBaseUrl}
          selection={bookScopeSelection}
        />
        <button
          type="button"
          className={`${pillSnapshot} !cursor-pointer hover:!bg-surface-hover hover:!border-border-strong hover:!text-text`}
          onClick={onShowHelp}
          title="Keyboard shortcuts (?)"
        >
          ? shortcuts
        </button>
      </div>
    </header>
  );
}
